import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useApi } from '../contexts/ApiContext';
import { toastError, toastSuccess } from '../utils/toast';
import { useAuth } from '../contexts/AuthContext';
import { AuthTypes } from '@flashbacktech/flashbackclient/api';
import PasswordInput from '../components/PasswordInput';

const VerifyEmail: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { apiClient } = useApi();

  const [status, setStatus] = useState<'verifying' | 'success' | 'error' | 'no-token' | 'enter-password'>('verifying');
  const [errorMessage, setErrorMessage] = useState('');
  const [errorMessage2, setErrorMessage2] = useState('');
  const [isResending, setIsResending] = useState(false);
  const [password, setPassword] = useState('');
  const [passwordConfirm, setPasswordConfirm] = useState('');
  const [verificationToken, setVerificationToken] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { setAuthState } = useAuth();
  const hasAttemptedVerification = useRef(false);

  useEffect(() => {
    const verifyEmail = async () => {
      // Prevent multiple verification attempts
      if (hasAttemptedVerification.current) {
        return;
      }

      const token = searchParams.get('token');

      if (!token) {
        setStatus('no-token');
        return;
      }

      hasAttemptedVerification.current = true;

      try {
        const preVerifyResponse = await apiClient.preVerifyEmail(token);
        console.log('@@@ verifyEmail: preVerifyResponse', preVerifyResponse);
        if (!preVerifyResponse.success) {
          setStatus('error');
          setErrorMessage(preVerifyResponse.message);
          toastError(preVerifyResponse.message);
          return;
        }
        console.log('@@@ verifyEmail: token', token);
        setVerificationToken(token);
        if (preVerifyResponse.authMethod === 'LOCAL' && !preVerifyResponse.hasPassword) {
          setStatus('enter-password');
          return;
        }
        handleValidateRegistration(preVerifyResponse.authMethod, preVerifyResponse.hasPassword);
      } catch (error) {
        setStatus('error');
        setErrorMessage('The verification token has either been already used, or it is expired or is invalid.');
        setErrorMessage2('If your user is already verified, please go to the login page.');
      }
    };

    verifyEmail();
  }, [searchParams, apiClient, navigate, setAuthState]);

  const handleValidateRegistration = async (authMethod: string, hasPassword: boolean) => {
    if (authMethod === 'LOCAL' && !hasPassword) {
      if (!password) {
        toastError('Please enter a password');
        return;
      }

      if (password !== passwordConfirm) {
        toastError('Passwords do not match');
        return;
      }
    }

    setIsSubmitting(true);
    try {
      // Call the validateRegistration API
      const tokenToUse = verificationToken || searchParams.get('token') || '';
      const response = await apiClient.validateRegistration(tokenToUse, password);

      if (response.success) {
        setStatus('success');
        console.log('@@@ verifyEmail: response', authMethod);
        if (authMethod === 'LOCAL') {
          setAuthState({
            user: {
              email: response.user?.email || '',
              name: response.user?.name || '',
              imageUrl: '',
            },
            token: response.accessToken || null,
            accessToken: response.accessToken || null,
            refreshToken: response.refreshToken || null,
            expiresAt: response.expiresAt || null,
          });
          setTimeout(() => {
              navigate('/hub');
            }, 2000);
        }
        else {
          setTimeout(() => {
            navigate('/login');
          }, 2000);
        }
      } else {
        toastError(response.message || 'Failed to set password');
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to set password';
      toastError(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleResendEmail = async () => {
    const uid = searchParams.get('uid');
    
    if (!uid) {
      toastError('User ID is missing');
      return;
    }

    setIsResending(true);
    try {
      const response: AuthTypes.ResendVerificationEmailResponse = await apiClient.resendVerificationEmail(uid);
      if (response.success) {
        toastSuccess(response.message || 'Verification email has been resent successfully');
      } else {
        toastError(response.message || 'Failed to resend verification email');
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to resend verification email';
      toastError(message);
    } finally {
      setIsResending(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50">
      <div className="mb-6">
        <img
          src="/flashgate-logo-black.png"
          alt="Flashgate logo"
          className="h-8"
          onClick={() => navigate('/')}
          style={{ cursor: 'pointer' }}
        />
      </div>

      <div className="w-full max-w-md text-center">
        {status === 'verifying' && (
          <>
            <h2 className="text-2xl font-medium text-gray-900 mb-4">We are verifying your email</h2>
            <div className="flex justify-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
            </div>
            <p className="text-gray-600 mt-4">Please wait while we verify your email address...</p>
          </>
        )}

        {status === 'success' && (
          <>
            <h2 className="text-2xl font-medium text-green-600 mb-4">
              Email Verified Successfully!
            </h2>
            <div className="flex justify-center mb-4">
              <svg
                className="w-12 h-12 text-green-500"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M5 13l4 4L19 7"
                />
              </svg>
            </div>
            <p className="text-gray-600 mb-4">Your email has been verified successfully.</p>
            <p className="text-sm text-gray-500">Redirecting to dashboard...</p>
          </>
        )}

        {status === 'enter-password' && (
          <>
            <h2 className="text-2xl font-medium text-gray-900 mb-4">Set Your Password</h2>
            <div className="flex justify-center mb-4">
              <svg
                className="w-12 h-12 text-blue-500"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z"
                />
              </svg>
            </div>
            <p className="text-gray-600 mb-6">Please create a password for your account.</p>
            <div className="w-full text-left">
              <PasswordInput
                password={password}
                passwordConfirm={passwordConfirm}
                onPasswordChange={setPassword}
                onPasswordConfirmChange={setPasswordConfirm}
              />
            </div>
            <button
              onClick={() => handleValidateRegistration('LOCAL', false)}
              disabled={isSubmitting || !password || !passwordConfirm}
              className="w-full bg-black text-white py-2 rounded hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed mt-4"
            >
              {isSubmitting ? 'Setting Password...' : 'Submit'}
            </button>
          </>
        )}

        {status === 'no-token' && (
          <>
            <h2 className="text-2xl font-medium text-blue-600 mb-4">Thank you for registering!</h2>
            <div className="flex justify-center mb-4">
              <svg
                className="w-12 h-12 text-blue-500"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                />
              </svg>
            </div>
            <p className="text-gray-600 mb-4">We have sent you a verification email.</p>
            <p className="text-sm text-gray-500 mb-4">
              Once verified, you will be redirected to the dashboard.
            </p>
            {searchParams.get('uid') && (
              <div className="text-sm text-gray-600 mt-6">
                <span>Didn't receive an email? </span>
                <button
                  onClick={handleResendEmail}
                  disabled={isResending}
                  className="text-blue-600 hover:text-blue-800 underline disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isResending ? 'Sending...' : 'Re-send email'}
                </button>
              </div>
            )}
          </>
        )}

        {status === 'error' && (
          <>
            <h2 className="text-2xl font-medium text-red-600 mb-4">Verification Failed</h2>
            <div className="flex justify-center mb-4">
              <svg
                className="w-12 h-12 text-red-500"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </div>
            <p className="text-gray-600 mb-4">{errorMessage}</p>
            <p className="text-gray-600 mb-4">{errorMessage2}</p>
            <button
              onClick={() => navigate('/login')}
              className="w-full bg-black text-white py-2 rounded hover:bg-gray-800"
            >
              Go to Login
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default VerifyEmail;
